import { useState, useCallback } from "react";
import { IAlert } from "../../../../interfaces/alert";
import { SUCCESS, SUCCESS_ALERT_DURATION } from "../../../../utils/constants";
import { updateClientStatus } from "../services/internalClientsService";

export interface IClientToDisable {
  id: number;
  disabled: boolean;
  name: string;
}

interface IUseClientDisablingOptions {
  onSuccess: () => void;
  setAlert: (alert: IAlert) => void;
}

/**
 * Hook for managing client enable/disable state changes
 * Handles toggling, submitting, and resetting client status changes
 */
export const useClientDisabling = ({
  onSuccess,
  setAlert,
}: IUseClientDisablingOptions) => {
  const [clientsToDisable, setClientsToDisable] = useState<IClientToDisable[]>([]);
  const [resetValue, setResetValue] = useState(false);

  const handleDisableAction = useCallback(
    (disableValue: boolean, id: number, name: string) => {
      setClientsToDisable((current) => {
        const filtered = current.filter((item) => item.id !== id);
        return [...filtered, { id, disabled: disableValue, name }];
      });
    },
    []
  );

  const handleSubmitDisabling = useCallback(async () => {
    try {
      await updateClientStatus(clientsToDisable);
      setAlert({
        typeOfAlert: SUCCESS,
        message: "Los clientes han sido actualizados con éxito.",
        showAlert: true,
      });
      setClientsToDisable([]);
      onSuccess();
      setTimeout(() => setAlert({} as IAlert), SUCCESS_ALERT_DURATION);
    } catch (error) {
      setAlert({
        typeOfAlert: "error",
        message: error instanceof Error ? error.message : String(error),
        showAlert: true,
      });
    }
  }, [clientsToDisable, onSuccess, setAlert]);

  const resetChanges = useCallback(() => {
    setClientsToDisable([]);
    setResetValue((prev) => !prev);
  }, []);

  const getDisabledClientNames = useCallback(
    () => clientsToDisable.filter((c) => c.disabled).map((c) => c.name),
    [clientsToDisable]
  );

  const hasClientsToDisable = clientsToDisable.some((c) => c.disabled);

  return {
    clientsToDisable,
    resetValue,
    handleDisableAction,
    handleSubmitDisabling,
    resetChanges,
    getDisabledClientNames,
    hasClientsToDisable,
    hasChanges: clientsToDisable.length > 0,
  };
};
